import { useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const usePdfExport = () => {
    const exportToPdf = useCallback(async (contentRef, activeTab, currentTenantInfo) => {
        try {
            const element = contentRef.current;
            if (!element) return;
 
            const originalStyle = {
                maxHeight: element.style.maxHeight,
                overflow: element.style.overflow,
                height: element.style.height
            };

            element.style.maxHeight = 'none';
            element.style.overflow = 'visible';
            element.style.height = 'auto';

            await new Promise(resolve => setTimeout(resolve, 500));
            const allCards = element.querySelectorAll('.MuiCard-root');

            for (const card of allCards) {
                try {
                    if (typeof card.scrollIntoView === 'function') {
                        card.scrollIntoView({ block: 'center', inline: 'center' });
                        await new Promise(r => setTimeout(r, 150));
                    }
                } catch (e) {
                }
            }
            
            try {
                const lastCard = allCards[allCards.length - 1];
                if (lastCard && typeof lastCard.scrollIntoView === 'function') {
                    lastCard.scrollIntoView({ block: 'end', inline: 'center' });
                    await new Promise(r => setTimeout(r, 300));
                }
            } catch (e) {
            }
            
            let leftColumn = [];
            let rightColumn = [];
            let aLeftColumn = [];
            let aRightColumn = [];
            let tLeftColumn = [];
            let tRightColumn = [];
            organizeCards(activeTab, allCards, leftColumn, rightColumn, aLeftColumn, aRightColumn, tLeftColumn, tRightColumn);

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            addPdfHeader(pdf, activeTab, pdfWidth);

            const layout = {
                sideMargin: 20,
                columnGap: 15,
                availableWidth: pdfWidth - (2 * 20),
                columnWidth: (pdfWidth - (2 * 20) - 15) / 2,
                leftColumnX: 20,
                rightColumnX: 20 + ((pdfWidth - (2 * 20) - 15) / 2) + 15
            };
            addTenantInfo(pdf, currentTenantInfo, layout);

            let currentY = 40 + 28 + 25;
            const cardMargin = activeTab === 1 ? 30 : 15;
            const addCardToPDF = async (card, x, y, width) => {
                return await captureCardToPdf(card, pdf, x, y, width);
            };

            await addFirstPage(activeTab, leftColumn, rightColumn, addCardToPDF, layout, currentY, cardMargin, pdfHeight);
            pdf.setFontSize(12);
            pdf.text(`Pagina 1`, pdfWidth / 2, pdfHeight - 15, { align: 'center' });
            await addSecondPage(activeTab, aLeftColumn, aRightColumn, addCardToPDF, layout, pdf, pdfHeight, pdfWidth);
            await addThirdPage(activeTab, tLeftColumn, tRightColumn, addCardToPDF, layout, pdf, pdfHeight, pdfWidth);

            element.style.maxHeight = originalStyle.maxHeight;
            element.style.overflow = originalStyle.overflow;
            element.style.height = originalStyle.height;
            pdf.compress = true;

            const fileName = getFileName(activeTab);
            pdf.save(`${fileName}_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Errore durante l\'esportazione PDF:', error);
            throw error;
        }
    }, []);

    return { exportToPdf };
};

const organizeCards = (activeTab, allCards, leftColumn, rightColumn, aLeftColumn, aRightColumn, tLeftColumn = [], tRightColumn = []) => {
    if (activeTab === 0) {
        const generalCards = Array.from(allCards).slice(0, 3);
        const aPointCards = Array.from(allCards).slice(3, 7);
        
        generalCards.forEach((card, index) => {
            if (index % 2 === 0) {
                leftColumn.push(card);
            } else {
                rightColumn.push(card);
            }
        });
        
        aPointCards.forEach((card, index) => {
            if (index < 2) {
                aLeftColumn.push(card);
            } else {
                aRightColumn.push(card);
            }
        });
    } else if (activeTab === 1) {
        const allCardArray = Array.from(allCards);
        const firstPageCards = allCardArray.slice(0, 2);
        firstPageCards.forEach((card, index) => {
            if (index % 2 === 0) {
                leftColumn.push(card);
            } else {
                rightColumn.push(card);
            }
        });
        const secondPageCards = allCardArray.slice(2, 8);
        secondPageCards.forEach((card, index) => {
            if (index % 2 === 0) {
                aLeftColumn.push(card);
            } else {
                aRightColumn.push(card);
            }
        });
        const thirdPageCards = allCardArray.slice(8);
        thirdPageCards.forEach((card, index) => {
            if (index % 2 === 0) {
                tLeftColumn.push(card);
            } else {
                tRightColumn.push(card);
            }
        });
    } else if (activeTab === 2) {
        const cPointCards = Array.from(allCards);
        if (cPointCards.length >= 2) {
            leftColumn.push(cPointCards[0]);
            rightColumn.push(cPointCards[1]);
        }
        if (cPointCards.length >= 3) {
            leftColumn.push(cPointCards[2]);
        }
    }
};
const addPdfHeader = (pdf, activeTab, pdfWidth) => {
    pdf.setFontSize(24);
    pdf.setFont(undefined, 'bold');
    let headerTitle = 'Statistiche Clienti - Autovalutazione';
    if (activeTab === 1) {
        headerTitle = 'Statistiche Prestazioni - Autovalutazione';
    } else if (activeTab === 2) {
        headerTitle = 'Statistiche Autovalutazioni';
    }
    pdf.text(headerTitle, pdfWidth / 2, 25, { align: 'center' });
};
const addTenantInfo = (pdf, currentTenantInfo, layout) => {
    const tenantBoxY = 40;
    const tenantBoxHeight = 40;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.rect(layout.sideMargin, tenantBoxY, layout.availableWidth, tenantBoxHeight);
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    const tenantLeftX = layout.sideMargin + 5;     
    pdf.text(`Ragione Sociale: ${currentTenantInfo.ragioneSociale}`, tenantLeftX, tenantBoxY + 8);
    pdf.text(`Soggetto Obbligato : ${currentTenantInfo.azienda}`, tenantLeftX, tenantBoxY + 15);
    pdf.text(`Partita IVA: ${currentTenantInfo.partitaIva}`, tenantLeftX, tenantBoxY + 22);
    pdf.text(`Sede: ${currentTenantInfo.sede} `, tenantLeftX, tenantBoxY + 29);
    pdf.text(`Data di generazione: ${new Date().toLocaleDateString('it-IT')}` , tenantLeftX, tenantBoxY + 36);
};
const captureCardToPdf = async (card, pdf, x, y, width) => {
    const originalBoxShadow = card.style.boxShadow;
    card.style.boxShadow = '0px 1px 3px rgba(0, 0, 0, 0.1)';
    card.style.backgroundColor = '#ffffff'; 
    const originalVisibility = card.style.visibility;
    const originalDisplay = card.style.display;
    try {
        card.style.visibility = 'visible';
        if (getComputedStyle(card).display === 'none') {
            card.style.display = 'block';
        }
        if (typeof card.scrollIntoView === 'function') {
            card.scrollIntoView({ block: 'center', inline: 'center' });
        }
        await new Promise((r) => setTimeout(r, 500));
        let canvas = await html2canvas(card, {
            scale: Math.min(2, window.devicePixelRatio || 1),
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            removeContainer: true,
            logging: false,
            width: card.offsetWidth,
            height: card.offsetHeight,
            preserveDrawingBuffer: true
        });
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
            try {
                const clone = card.cloneNode(true);
                const copyStyles = (source, target) => {
                    const computed = window.getComputedStyle(source);
                    for (let i = 0; i < computed.length; i++) {
                        const prop = computed[i];
                        try {
                            target.style.setProperty(prop, computed.getPropertyValue(prop), computed.getPropertyPriority(prop));
                        } catch (e) {
                        }
                    }
                };

                const inlineAllStyles = (srcRoot, tgtRoot) => {
                    copyStyles(srcRoot, tgtRoot);
                    const srcChildren = Array.from(srcRoot.children || []);
                    const tgtChildren = Array.from(tgtRoot.children || []);
                    for (let i = 0; i < srcChildren.length; i++) {
                        inlineAllStyles(srcChildren[i], tgtChildren[i]);
                    }
                };
                inlineAllStyles(card, clone);

                const offscreen = document.createElement('div');
                offscreen.style.position = 'fixed';
                offscreen.style.left = '-9999px';
                offscreen.style.top = '-9999px';
                offscreen.style.width = `${card.offsetWidth}px`;
                offscreen.style.height = `${card.offsetHeight}px`;
                offscreen.style.overflow = 'visible';
                offscreen.appendChild(clone);
                document.body.appendChild(offscreen);
                await new Promise((r) => setTimeout(r, 80));

                canvas = await html2canvas(clone, {
                    scale: Math.min(2, window.devicePixelRatio || 1),
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    removeContainer: true,
                    logging: false,
                    width: clone.offsetWidth || card.offsetWidth,
                    height: clone.offsetHeight || card.offsetHeight,
                    preserveDrawingBuffer: true
                });
                document.body.removeChild(offscreen);
            } catch (cloneErr) {
            }
        }

        if (!canvas || canvas.width === 0 || canvas.height === 0) {
            return 0;
        }
        const imgData = canvas.toDataURL('image/jpeg', 0.88);
        const scale = width / canvas.width;
        const scaledHeight = canvas.height * scale;

        try {
            pdf.addImage(imgData, 'JPEG', x, y, width, scaledHeight);
            return scaledHeight;
        } catch (e) {
            throw e;
        }
    } finally {
        card.style.boxShadow = originalBoxShadow;
        card.style.visibility = originalVisibility;
        card.style.display = originalDisplay;
    }
};
const addFirstPage = async (activeTab, leftColumn, rightColumn, addCardToPDF, layout, currentY, cardMargin, pdfHeight) => {
    if (activeTab === 0 || activeTab === 1) {
        for (let i = 0; i < leftColumn.length; i++) {
            if (i < leftColumn.length) {
                const cardHeight = await addCardToPDF(leftColumn[i], layout.leftColumnX, currentY, layout.columnWidth);
                
                if (i < rightColumn.length) {
                    await addCardToPDF(rightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
                }
                
                currentY += cardHeight + cardMargin;
                
                if (currentY > pdfHeight - 60) {
                    break;
                }
            }
        }
    } else if (activeTab === 2) {
        if (leftColumn.length >= 2) {
            const cardHeight1 = await addCardToPDF(leftColumn[0], layout.leftColumnX, currentY, layout.columnWidth);
            await addCardToPDF(rightColumn[0], layout.rightColumnX, currentY, layout.columnWidth);
            currentY += cardHeight1 + cardMargin;
            await addCardToPDF(leftColumn[1], layout.leftColumnX, currentY, layout.columnWidth);
        } else if (leftColumn.length === 1) {
            await addCardToPDF(leftColumn[0], layout.leftColumnX, currentY, layout.columnWidth);
        }
    }
    return currentY;
};
const addSecondPage = async (activeTab, aLeftColumn, aRightColumn, addCardToPDF, layout, pdf, pdfHeight, pdfWidth) => {
    if ((activeTab === 0 || activeTab === 1) && (aLeftColumn.length > 0 || aRightColumn.length > 0)) {
        pdf.addPage();
        let currentY = 30;
        const cardMargin = 5;
        
        if (activeTab === 0) {
            for (let i = 0; i < aLeftColumn.length; i++) {
                if (i < aLeftColumn.length) {
                    const cardHeight = await addCardToPDF(aLeftColumn[i], layout.leftColumnX, currentY, layout.columnWidth);
                    
                    if (i < aRightColumn.length) {
                        await addCardToPDF(aRightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
                    }
                    
                    currentY += cardHeight + cardMargin;
                    
                    if (currentY > pdfHeight - 60) {
                        break;
                    }
                }
            }
        } else if (activeTab === 1) {
            for (let i = 0; i < Math.max(aLeftColumn.length, aRightColumn.length); i++) {
                if (i < aLeftColumn.length) {
                    const cardHeight = await addCardToPDF(aLeftColumn[i], layout.leftColumnX, currentY, layout.columnWidth);
                    if (i < aRightColumn.length) {
                        await addCardToPDF(aRightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
                    }
                    currentY += cardHeight + cardMargin;
                    if (currentY > pdfHeight - 60) {
                        break;
                    }
                } else if (i < aRightColumn.length) {
                    await addCardToPDF(aRightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
                }
            }
        }
        pdf.setFontSize(12);
        pdf.text(`Pagina 2`, pdfWidth / 2, pdfHeight - 15, { align: 'center' });
    }
};
const addThirdPage = async (activeTab, tLeftColumn, tRightColumn, addCardToPDF, layout, pdf, pdfHeight, pdfWidth) => {
    if (activeTab === 1 && (tLeftColumn.length > 0 || tRightColumn.length > 0)) {
        pdf.addPage();
        let currentY = 30;
        const cardMargin = 5;
        
        for (let i = 0; i < Math.max(tLeftColumn.length, tRightColumn.length); i++) {
            if (i < tLeftColumn.length) {
                const cardHeight = await addCardToPDF(tLeftColumn[i], layout.leftColumnX, currentY, layout.columnWidth);
                if (i < tRightColumn.length) {
                    await addCardToPDF(tRightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
                }
                currentY += cardHeight + cardMargin;
                if (currentY > pdfHeight - 60) {
                    break;
                }
            } else if (i < tRightColumn.length) {
                await addCardToPDF(tRightColumn[i], layout.rightColumnX, currentY, layout.columnWidth);
            }
        }
        pdf.setFontSize(12);
        pdf.text(`Pagina 3`, pdfWidth / 2, pdfHeight - 15, { align: 'center' });
    }
};
const getFileName = (activeTab) => {
    if (activeTab === 1) {
        return 'statistiche_prestazioni';
    } else if (activeTab === 2) {
        return 'statistiche_autovalutazioni';
    }
    return 'statistiche_clienti';
};